import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Code2, 
  Database, 
  Lightbulb, 
  Users, 
  ArrowRight, 
  CheckCircle2,
  ChevronRight
} from 'lucide-react';
import { serviceAPI } from '../services/api';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await serviceAPI.getAll();
      // If no services in database, use default data
      if (response.data.services.length === 0) {
        setServices(defaultServices);
      } else {
        setServices(response.data.services);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices(defaultServices);
    } finally {
      setLoading(false);
    }
  };

  const defaultServices = [
    {
      _id: '1',
      slug: 'it-development',
      name: 'IT Development & Maintenance',
      category: 'IT Development',
      shortDescription: 'Build, enhance, and maintain mission-critical applications with enterprise-grade quality.',
      features: [
        { title: 'Web Application Development', description: 'Scalable, secure, and user-centric web solutions' },
        { title: 'Custom Software Development', description: 'Tailored applications for your unique business needs' },
        { title: 'Application Support', description: 'Keep your systems running at peak performance' },
        { title: 'Legacy Modernization', description: 'Transform outdated systems into modern architectures' }
      ],
      icon: 'Code2'
    },
    {
      _id: '2',
      slug: 'erp-solutions',
      name: 'ERP Development & Maintenance',
      category: 'ERP Solutions',
      shortDescription: 'Maximize your ERP investment with certified expertise across all major platforms.',
      features: [
        { title: 'Oracle Solutions', description: 'ERP Cloud, E-Business Suite implementation and support' },
        { title: 'SAP Solutions', description: 'S/4HANA, ECC implementation and optimization' },
        { title: 'Salesforce', description: 'CRM optimization and custom development' },
        { title: 'Microsoft Dynamics', description: 'Business applications tailored to your needs' }
      ],
      icon: 'Database'
    },
    {
      _id: '3',
      slug: 'it-consulting',
      name: 'IT Consulting',
      category: 'IT Consulting',
      shortDescription: 'Strategic technology guidance for digital transformation and enterprise architecture.',
      features: [
        { title: 'Digital Transformation', description: 'Roadmaps for enterprise modernization' },
        { title: 'ERP Advisory', description: 'Platform selection, implementation planning, optimization' },
        { title: 'Cloud Strategy', description: 'Migration planning and cloud-native architecture' },
        { title: 'Enterprise Architecture', description: 'Scalable, secure technology foundations' }
      ],
      icon: 'Lightbulb'
    },
    {
      _id: '4',
      slug: 'manpower-consulting',
      name: 'Manpower Consulting',
      category: 'Manpower Consulting',
      shortDescription: 'Access top technology talent on demand with flexible engagement models.',
      features: [
        { title: 'IT Staff Augmentation', description: 'Scale your team with pre-vetted professionals' },
        { title: 'Dedicated Teams', description: 'Full teams focused on your projects' },
        { title: 'ERP Specialists', description: 'Platform-certified consultants on demand' },
        { title: 'Contract & Permanent', description: 'Flexible placement options' }
      ],
      icon: 'Users'
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Code2: <Code2 className="w-8 h-8" />,
      Database: <Database className="w-8 h-8" />,
      Lightbulb: <Lightbulb className="w-8 h-8" />,
      Users: <Users className="w-8 h-8" />
    };
    return icons[iconName] || <Code2 className="w-8 h-8" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 gradient-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Enterprise Technology Solutions
            </h1>
            <p className="text-lg text-white/80">
              Comprehensive IT services designed to accelerate your digital transformation 
              and maximize technology ROI. From development to consulting, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              What We Offer
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our four pillars of service delivery ensure comprehensive coverage 
              for all your technology needs.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={service._id}
                variants={fadeInUp}
                className="card-hover p-8"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center text-accent flex-shrink-0">
                    {getIcon(service.icon)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-primary mb-2">{service.name}</h3>
                    <p className="text-slate-600 mb-4">{service.shortDescription}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features?.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-teal flex-shrink-0" />
                          {feature.title}
                        </li>
                      ))}
                    </ul>

                    <Link 
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center text-accent font-semibold hover:text-accent-dark transition-colors"
                    >
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              How We Work
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A proven methodology that ensures successful project delivery every time.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-4 gap-8"
          >
            {[
              { step: '01', title: 'Discovery', description: 'We understand your business, challenges, and goals.' },
              { step: '02', title: 'Strategy', description: 'We design a tailored solution aligned with your objectives.' },
              { step: '03', title: 'Execution', description: 'We deliver with agility, transparency, and quality.' },
              { step: '04', title: 'Support', description: 'We provide ongoing support to ensure long-term success.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="w-16 h-16 bg-accent text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-primary mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="section gradient-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Technologies</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4">
              Platforms We Master
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Certified expertise across the industry's leading enterprise platforms.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { name: 'Oracle', category: 'ERP' },
              { name: 'SAP', category: 'ERP' },
              { name: 'Salesforce', category: 'CRM' },
              { name: 'Microsoft Dynamics', category: 'ERP' },
              { name: 'AWS', category: 'Cloud' },
              { name: 'Azure', category: 'Cloud' },
              { name: 'React', category: 'Frontend' },
              { name: 'Node.js', category: 'Backend' }
            ].map((tech, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="p-6 bg-white/10 backdrop-blur-sm rounded-xl text-center hover:bg-white/20 transition-colors"
              >
                <p className="text-xs text-teal uppercase tracking-wider mb-1">{tech.category}</p>
                <p className="font-semibold">{tech.name}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Not Sure What You Need?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Let's discuss your challenges and find the right solution together.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn bg-white text-accent hover:bg-slate-100 btn-lg">
                Schedule a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="btn bg-transparent text-white hover:bg-white/10 border border-white/30 btn-lg">
                Request a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
